// Utilitários para limpeza de cache do sistema AI School
import { supabase } from '../lib/supabase';

/**
 * Limpa todo o cache relacionado ao sistema AI School
 * @param {boolean} forceReload - Se deve forçar recarregamento da página
 * @returns {Promise<Object>} Resultado da operação
 */
export const clearAISchoolCache = async (forceReload = false) => {
  try {
    console.log('🧹 Iniciando limpeza completa de cache...');
    
    const results = {
      localStorage: 0,
      sessionStorage: 0,
      supabase: false,
      browserCache: 0,
      context: false
    };

    // 1. Limpar localStorage
    const localStorageKeys = [
      'supabase.auth.token',
      'supabase.auth.expires_at',
      'supabase.auth.refresh_token',
      'aischool_user_data',
      'aischool_auth_state',
      'aischool_cache_timestamp',
      'aischool_settings',
      'aischool_preferences'
    ];
    
    localStorageKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        results.localStorage++;
      }
    });

    // 2. Limpar sessionStorage
    const sessionStorageKeys = [
      'supabase.auth.token',
      'aischool_session_data',
      'aischool_temp_data'
    ];
    
    sessionStorageKeys.forEach(key => {
      if (sessionStorage.getItem(key)) {
        sessionStorage.removeItem(key);
        results.sessionStorage++;
      }
    });

    // 3. Limpar cache do Supabase
    try {
      await supabase.auth.signOut();
      results.supabase = true;
      console.log('🔄 Sessão do Supabase limpa');
    } catch (error) {
      console.log('ℹ️ Sessão do Supabase já estava limpa');
    }

    // 4. Limpar cache do navegador
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        const aischoolCaches = cacheNames.filter(name => 
          name.includes('aischool') || 
          name.includes('static') || 
          name.includes('auth')
        );
        
        await Promise.all(
          aischoolCaches.map(name => caches.delete(name))
        );
        
        results.browserCache = aischoolCaches.length;
        console.log(`🗑️ Removidos ${aischoolCaches.length} caches do navegador`);
      } catch (cacheError) {
        console.log('ℹ️ Cache do navegador não pôde ser limpo:', cacheError.message);
      }
    }

    // 5. Limpar dados do contexto global
    if (window.aischoolAuthContext) {
      delete window.aischoolAuthContext;
      results.context = true;
      console.log('🗑️ Contexto de autenticação limpo');
    }

    // 6. Limpar cookies relacionados (se existirem)
    const cookiesToRemove = [
      'aischool_auth',
      'aischool_session',
      'supabase_auth'
    ];
    
    cookiesToRemove.forEach(cookieName => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });

    console.log('✅ Cache limpo com sucesso!', results);
    
    // 7. Forçar recarregamento se solicitado
    if (forceReload) {
      console.log('🔄 Recarregando página...');
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
    
    return {
      success: true,
      message: 'Cache limpo com sucesso!',
      results
    };
    
  } catch (error) {
    console.error('❌ Erro ao limpar cache:', error);
    return {
      success: false,
      message: 'Erro ao limpar cache',
      error: error.message
    };
  }
};

/**
 * Limpa apenas o cache de autenticação
 * @returns {Promise<Object>} Resultado da operação
 */
export const clearAuthCache = async () => {
  try {
    console.log('🔐 Limpando cache de autenticação...');
    
    // Limpar tokens do Supabase
    await supabase.auth.signOut();
    
    // Limpar dados de auth do localStorage
    const authKeys = [
      'supabase.auth.token',
      'supabase.auth.expires_at',
      'supabase.auth.refresh_token',
      'aischool_auth_state'
    ];
    
    authKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
      }
    });
    
    console.log('✅ Cache de autenticação limpo!');
    return { success: true, message: 'Cache de autenticação limpo!' };
    
  } catch (error) {
    console.error('❌ Erro ao limpar cache de autenticação:', error);
    return { success: false, message: 'Erro ao limpar cache de autenticação' };
  }
};

/**
 * Verifica se há dados de cache antigos
 * @returns {boolean} True se há cache antigo
 */
export const hasOldCache = () => {
  const cacheTimestamp = localStorage.getItem('aischool_cache_timestamp');
  if (!cacheTimestamp) return false;
  
  const cacheAge = Date.now() - parseInt(cacheTimestamp);
  const maxAge = 24 * 60 * 60 * 1000; // 24 horas
  
  return cacheAge > maxAge;
};

/**
 * Marca o cache como atualizado
 */
export const markCacheAsFresh = () => {
  localStorage.setItem('aischool_cache_timestamp', Date.now().toString());
  console.log('📅 Cache marcado como atualizado');
};

/**
 * Limpa cache específico por tipo
 * @param {string} cacheType - Tipo de cache a limpar
 * @returns {Promise<Object>} Resultado da operação
 */
export const clearSpecificCache = async (cacheType) => {
  switch (cacheType) {
    case 'auth':
      return await clearAuthCache();
    case 'user_data':
      localStorage.removeItem('aischool_user_data');
      localStorage.removeItem('aischool_auth_state');
      return { success: true, message: 'Cache de dados do usuário limpo!' };
    case 'settings':
      localStorage.removeItem('aischool_settings');
      localStorage.removeItem('aischool_preferences');
      return { success: true, message: 'Cache de configurações limpo!' };
    default:
      return { success: false, message: 'Tipo de cache não reconhecido' };
  }
};

export default {
  clearAISchoolCache,
  clearAuthCache,
  hasOldCache,
  markCacheAsFresh,
  clearSpecificCache
};
